'use client';

import NewPlaceHolderImg from '@/assets/icons/PlaceHolderImg.svg';
import CDNImage from '@/components/cdnImage/cdnImage';
import ImageZoom from '@/components/ImageZoom';
import Player from '@/components/player';
import { CN_PRODUCT } from '@/components/product/const';
import { sendClickTrack } from '@/tracks/25217';
import { DP_EVENT_NAMES, dpBusinessLogger } from '@/utils/dpUtils/businessLogger';
import { imageUtil } from '@/utils/imageUtil';
import { memo } from 'react';

interface MainMediaRendererProps {
  isVideo: boolean;
  targetSrc: string;
  sku: string;
  mainImageLoaded: boolean;
  setMainImageLoaded: (loaded: boolean) => void;
}

const MainMediaRenderer = memo(
  ({ isVideo, targetSrc, sku, mainImageLoaded, setMainImageLoaded }: MainMediaRendererProps) => {
    const onMainImageLoadError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      dpBusinessLogger({
        eventName: DP_EVENT_NAMES.MAIN_IMAGE_LOAD_ERROR,
        message: {
          mainSkuId: sku,
          imageUrl: e?.currentTarget?.src,
        },
      });
    };

    const onPlayError = () => {
      dpBusinessLogger({
        eventName: DP_EVENT_NAMES.MAIN_VIDEO_PLAY_ERROR,
        message: {
          mainSkuId: sku,
          videoUrl: targetSrc,
        },
      });
    };

    return isVideo ? (
      <div style={{ position: 'relative' }}>
        {/* 主图占位图 - 确保优先加载 */}
        <NewPlaceHolderImg
          style={{
            opacity: mainImageLoaded ? 0 : 1,
            transition: 'opacity 0.3s',
            position: mainImageLoaded ? 'absolute' : 'relative',
          }}
        />

        {/* 真实主图 在占位图渲染完成之后展示 */}
        <Player
          url={`${targetSrc}`}
          width={624}
          height={624}
          style={{
            opacity: mainImageLoaded ? 1 : 0,
            position: 'absolute',
            top: 0,
            left: 0,
            transition: 'opacity 0.3s',
          }}
          onPlayTrack={(status) => {
            sendClickTrack.Productdetail_BigVideo_Click({
              mainskuid: sku,
              playStatus: status,
            });
          }}
          onVideoReady={() => {
            setMainImageLoaded(true);
          }}
          onError={onPlayError}
        />
      </div>
    ) : (
      <ImageZoom
        imgUrl={imageUtil.concatImgUrlWithoutForceHttps(targetSrc, 1248, 1248, 70)}
        containerWidth={624}
        containerHeight={624}
        zoomedAreaWidth={312}
        zoomedAreaHeight={411}
        zoomedImageBoxWidth={496}
      >
        <div className={CN_PRODUCT('big-img')} style={{ position: 'relative' }}>
          {/* 主图占位图 - 确保优先加载 */}
          <NewPlaceHolderImg
            style={{
              opacity: mainImageLoaded ? 0 : 1,
              transition: 'opacity 0.3s',
              position: mainImageLoaded ? 'absolute' : 'relative',
            }}
          />

          <CDNImage
            src={targetSrc}
            width={624}
            height={624}
            alt='thumbnail'
            style={{
              opacity: mainImageLoaded ? 1 : 0,
              position: 'absolute',
              top: 0,
              left: 0,
              transition: 'opacity 0.3s',
            }}
            onLoad={() => {
              setMainImageLoaded(true);
            }}
            onError={onMainImageLoadError}
          />
        </div>
      </ImageZoom>
    );
  },
);

MainMediaRenderer.displayName = 'MainMediaRenderer';

export default MainMediaRenderer;
